import { AlertTriangle, CheckCircle2, Info } from "lucide-react";
import { useTranslation } from "react-i18next";

import type { BaseComponentProps } from "../types";

type StatusVariant = "success" | "warning" | "error" | "neutral" | "information";

export interface InlineStatusProps {
  dataKey: string;
}

type InlineStatusComponentProps = BaseComponentProps & InlineStatusProps;

const variantConfig: Record<StatusVariant, { icon: typeof Info; className: string }> = {
  success: { icon: CheckCircle2, className: "text-emerald-500" },
  warning: { icon: AlertTriangle, className: "text-amber-500" },
  error: { icon: AlertTriangle, className: "text-red-500" },
  neutral: { icon: Info, className: "text-muted-foreground" },
  information: { icon: Info, className: "text-blue-500" },
};

function InlineStatusComponent({ dataKey, data }: InlineStatusComponentProps) {
  const { t } = useTranslation();

  const resolved = dataKey && data ? data[dataKey] : undefined;
  if (!resolved) return null;

  const resolvedObj = typeof resolved === "object" && resolved !== null
    ? (resolved as Record<string, unknown>)
    : undefined;

  const label = resolvedObj?.label != null ? String(resolvedObj.label) : String(resolved);
  const variant = (resolvedObj?.variant != null
    ? String(resolvedObj.variant).toLowerCase()
    : "neutral") as StatusVariant;

  const displayText = t(label, { defaultValue: label });
  if (!displayText) return null;

  const config = variantConfig[variant] ?? variantConfig.neutral;
  const Icon = config.icon;

  return (
    <span className={`inline-flex items-center gap-1.5 text-sm font-medium ${config.className}`}>
      <Icon className="size-3.5" />
      {displayText}
    </span>
  );
}

export { InlineStatusComponent };
